/** One added activity's gates: reorder, mode, remove and overlap flags. Shared by Activity and Access. */

import type { Gate } from "@nebulize/sdk";

import { Button, IconButton, Stack, StatusBadge } from "@/components/ui";

import { useGates, useSetGates } from "../activities/lib/queries";
import { DownIcon, UpIcon } from "../editor/LayerIcons";
import { Loaded, Section } from "../editor/tray/Section";
import { TrayRow } from "../editor/tray/TrayRow";

import { Mono, Muted } from "./Text";

/** What one gate names as its target: one submission, optionally narrowed, or one canvas. */
function targetOf(gate: Gate): string {
  const submission = gate.submission ?? "";
  if (submission !== "") {
    const values = gate.values ?? [];
    return values.length === 0 ? submission : `${submission} = ${values.join(", ")}`;
  }
  return gate.canvas ?? "";
}

export interface GatesEditorProps {
  readonly addedActivityId: string;
  /** Wraps the list in its own titled section, and hides entirely once loaded with nothing gated. */
  readonly title?: string;
  /** A label above the list with no section chrome, for one row among several on one screen. */
  readonly heading?: string;
}

/** One added activity's gates, in the order they resolve. The order decides which one fires. */
export function GatesEditor({ addedActivityId, title, heading }: GatesEditorProps) {
  const gates = useGates(addedActivityId);
  const setGates = useSetGates(addedActivityId);
  const rows = gates.data ?? [];

  if (title !== undefined && rows.length === 0) return null;

  const write = (next: Gate[]): void => {
    setGates.mutate(next);
  };

  const move = (index: number, by: number): void => {
    const to = index + by;
    if (to < 0 || to >= rows.length) return;
    const next = [...rows];
    const held = next[index];
    const other = next[to];
    if (held === undefined || other === undefined) return;
    next[index] = other;
    next[to] = held;
    write(next);
  };

  const overlapping = (gate: Gate, at: number): boolean =>
    rows.some((one, index) => index !== at && targetOf(one) === targetOf(gate));

  const body = (
    <Stack gap={3}>
      {heading === undefined ? null : <div className="cx-node-name">{heading}</div>}
      <Loaded query={gates} rows={2}>
        {() =>
          rows.length === 0 ? (
            <Muted small>no gate</Muted>
          ) : (
            <>
              {rows.map((gate, index) => (
                <TrayRow
                  key={`${targetOf(gate)}${String(index)}`}
                  title={
                    <span className="cx-gate-title">
                      <span className="cx-gate-order">{index + 1}</span>
                      {gate.label === undefined || gate.label === "" ? targetOf(gate) : gate.label}
                    </span>
                  }
                  badge={
                    overlapping(gate, index) ? <StatusBadge tone="warning">overlaps</StatusBadge> : undefined
                  }
                  meta={
                    <Muted small>
                      <Mono>{gate.requires.join(" and ")}</Mono>
                    </Muted>
                  }
                  actions={
                    <>
                      <div className="cx-seg">
                        {(["show_locked", "hide"] as const).map((mode) => (
                          <button
                            type="button"
                            key={mode}
                            className={
                              mode === (gate.mode ?? "show_locked")
                                ? "cx-seg-btn cx-seg-btn-on"
                                : "cx-seg-btn"
                            }
                            onClick={() => {
                              write(rows.map((one, at) => (at === index ? { ...one, mode } : one)));
                            }}
                          >
                            {mode}
                          </button>
                        ))}
                      </div>
                      <div className="cx-layer-order">
                        <IconButton
                          size="sm"
                          label={`Move ${targetOf(gate)} earlier`}
                          disabled={setGates.isPending || index === 0}
                          onClick={() => {
                            move(index, -1);
                          }}
                        >
                          <UpIcon />
                        </IconButton>
                        <IconButton
                          size="sm"
                          label={`Move ${targetOf(gate)} later`}
                          disabled={setGates.isPending || index === rows.length - 1}
                          onClick={() => {
                            move(index, 1);
                          }}
                        >
                          <DownIcon />
                        </IconButton>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Remove the gate on ${targetOf(gate)}`}
                        loading={setGates.isPending}
                        onClick={() => {
                          write(rows.filter((_one, at) => at !== index));
                        }}
                      >
                        Remove
                      </Button>
                    </>
                  }
                />
              ))}
            </>
          )
        }
      </Loaded>
      {setGates.isError ? <StatusBadge tone="danger">{setGates.error.message}</StatusBadge> : null}
    </Stack>
  );

  return title === undefined ? body : <Section title={title}>{body}</Section>;
}
